import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/PPEus_FC.svg";
import { Link, useLocation } from "react-router-dom"; // Added useLocation

const Header: React.FC = () => {
  // State to control the visibility of the side menu
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [navFontSize, setNavFontSize] = useState("19px");
  
  // Get current location for active navigation
  const location = useLocation();

  // Handle font size based on window width
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 900) {
        setNavFontSize("12px");
      } else if (width <= 1100) {
        setNavFontSize("16px");
      } else {
        setNavFontSize("19px");
      }
    };

    // Set initial font size
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle body scroll when menu is open
  useEffect(() => {
    if (isSideMenuOpen) {
      document.body.style.overflow = "hidden"; // Prevent scrolling when menu is open
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isSideMenuOpen]);

  // Toggle side menu
  const toggleSideMenu = () => {
    setIsSideMenuOpen(!isSideMenuOpen);
  };

  const navLinks = [
    { href: "/covid-19-test-kits.html", label: "COVID-19 Test Kits" },
    { href: "/nitrile-gloves.html", label: "Nitrile Gloves" },
    { href: "/disposable-masks.html", label: "Disposable Masks" },
    { href: "/surface-wipes.html", label: "Surface Wipes" },
    { href: "/hand-sanitizer", label: "Hand Sanitizer" },
  ];

  // Function to check if link is active
  const isActiveLink = (href: string) => {
    return location.pathname === href;
  };

  return (
    <>
      {/* Main Content Wrapper - will be pushed when menu opens */}
      <motion.div
        animate={{
          x: isSideMenuOpen ? "90vw" : 0,
          scale: isSideMenuOpen ? 0.95 : 1,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
        }}
        className="relative z-10 bg-white"
      >
        <header className="w-full border-t-4 border-t-[#BA0C2F]">
          {/* Top Bar: Shows on both mobile and desktop */}
          <div className="w-full" style={{ backgroundColor: "#f0f0ed" }}>
            <div className="max-w-[1710px] mx-auto px-[20px] flex justify-between items-center min-h-[43.8px]">
              {/* Mobile and Desktop - Show all three items */}
              <div className="flex items-center justify-end space-x-10 text-[12px] text-[#999] tracking-wide leading-[1.4] w-full ml-auto">
                <span>WELCOME TO PPE.US!</span>
                <Link
                  to="/about-us"
                  className=" duration-200 text-[12px] text-[#999] tracking-wide leading-[1.4]"
                >
                  ABOUT US
                </Link>
                <Link
                  to="/contact"
                  className="duration-200 text-[12px] text-[#999] tracking-wide leading-[1.4]"
                >
                  CONTACT US
                </Link>
              </div>
            </div>
          </div>

          {/* Main Header Content: Fixed desktop layout */}
          <div className="w-full bg-white">
            <div className="max-w-[1710px] mx-auto px-[20px] lg:px-[20px] py-12 md:py-[23px]">
              {/* Mobile Header with Centered Logo and Side Menu Button */}
              <div className="relative w-full md:hidden flex items-center justify-between">
                {/* Side Menu Toggle Button - Left Side */}
                <button
                  onClick={toggleSideMenu}
                  className="flex items-center gap-2 p-2 -ml-2 rounded-md hover:bg-gray-100 focus:outline-none"
                  aria-label="Toggle Side Menu"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="#BB372E"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16m-7 6h7"
                    ></path>
                  </svg>
                </button>

                {/* Centered Logo */}
                <div className="absolute left-1/2 transform -translate-x-1/2">
                  <a href="/" className="flex-shrink-0">
                    <img
                      src={logo}
                      alt="PPE.US Logo"
                      className="rounded-md h-20"
                      loading="lazy"
                    />
                  </a>
                </div>

                {/* Empty div to balance the flex layout */}
                <div className="w-10"></div>
              </div>

              {/* Desktop Layout: Logo and Navigation side by side */}
              <div className="hidden md:flex items-center justify-between">
                {/* Logo */}
                <div className="flex items-center">
                  <a href="/" className="flex-shrink-0">
                    <img
                      src={logo}
                      alt="PPE.US Logo"
                      className="max-w-[180px] max-h-[106px] w-full h-full"
                      loading="lazy"
                    />
                  </a>
                </div>

                {/* Desktop Navigation Menu: Dynamic font size based on breakpoints */}
                <div className="flex items-center header-nav">
                  <nav>
                    <ul
                      className="flex space-x-2 md:space-x-4 lg:space-x-6 xl:space-x-[50px]"
                      style={{ fontSize: navFontSize }}
                    >
                      {navLinks.map((link) => (
                        <li key={link.href}>
                          <Link
                            to={link.href}
                            className={`transition-colors duration-200 whitespace-nowrap text-[18px] leading-[18px] !font-bold text-[#011d42] hover:text-[#C10230] ${
                              isActiveLink(link.href) ? "active" : ""
                            }`}
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </header>
      </motion.div>

      {/* Animated Side Menu Overlay */}
      <AnimatePresence>
        {isSideMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={toggleSideMenu}
            />

            {/* Side Menu Panel - 90% width on mobile */}
            <motion.div
              initial={{ x: "-90vw" }}
              animate={{ x: 0 }}
              exit={{ x: "-90vw" }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
              }}
              className="fixed left-0 top-0 h-full w-[90vw] bg-[#F8F8F8] shadow-xl z-50"
            >
              <div className="flex justify-between items-center">
                <div className="flex w-full">
                  <div className="flex-1 py-4 px-6 text-left pl-6 font-medium text-[#BB372E] bg-white">
                    Menu
                  </div>
                </div>
                <button
                  onClick={toggleSideMenu}
                  className="p-4 text-gray-600 hover:bg-transparent transition-colors"
                  aria-label="Close menu"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <ul className="py-2">
                  <li className="py-3 border-b border-gray-100 last:border-b-0">
                    <a
                      href="/covid-19-test-kits.html"
                      className="block text-gray-700 hover:text-black pl-6 py-2 transition-colors duration-200"
                      onClick={toggleSideMenu}
                    >
                      COVID-19 Test Kits
                    </a>
                  </li>
                  <li className="py-3 border-b border-gray-100 last:border-b-0">
                    <a
                      href="/nitrile-gloves.html"
                      className="block text-gray-700 hover:text-black pl-6 py-2 transition-colors duration-200"
                      onClick={toggleSideMenu}
                    >
                      Nitrile Gloves
                    </a>
                  </li>
                  <li className="py-3 border-b border-gray-100 last:border-b-0">
                    <a
                      href="/disposable-masks.html"
                      className="block text-gray-700 hover:text-black pl-6 py-2 transition-colors duration-200"
                      onClick={toggleSideMenu}
                    >
                      Disposable Masks
                    </a>
                  </li>
                  <li className="py-3 border-b border-gray-100 last:border-b-0">
                    <a
                      href="/surface-wipes.html"
                      className="block text-gray-700 hover:text-black pl-6 py-2 transition-colors duration-200"
                      onClick={toggleSideMenu}
                    >
                      Surface Wipes
                    </a>
                  </li>
                  <li className="py-3 border-b border-gray-100 last:border-b-0">
                    <a
                      href="/hand-sanitizer"
                      className="block text-gray-700 hover:text-black pl-6 py-2 transition-colors duration-200"
                      onClick={toggleSideMenu}
                    >
                      Hand Sanitizer
                    </a>
                  </li>
                </ul>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
